import React, {useState} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Grid,
  Slider,
  Divider,
} from "@material-ui/core";
import {CloudUpload as UploadIcon, CheckCircle as CheckIcon, Image as ImageIcon} from "@material-ui/icons";
import {toast} from "react-toastify";
import axios from "axios";

export default function AddWorkshopDialog({open, onClose}) {
  const [form, setForm] = useState({
    title: "",
    date: "",
    name_x: 2250,
    name_y: 2150,
    font_size: 64,
    font_color: "#000000",
  });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const API_URL = window?.location?.hostname?.includes("maroc")
    ? process.env.REACT_APP_API_MAROC
    : process.env.REACT_APP_API;

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.type.startsWith("image/")) {
        toast.error("Please upload an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        toast.error("File size should not exceed 5MB");
        return;
      }

      setFile(selectedFile);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);

      // Clear error
      if (errors.certificate) {
        setErrors({...errors, certificate: ""});
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!form.title.trim()) {
      newErrors.title = "Workshop title is required";
    }

    if (!form.date) {
      newErrors.date = "Workshop date is required";
    }

    if (!file) {
      newErrors.certificate = "Certificate template is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      return;
    }

    setSubmitting(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("date", form.date);
    fd.append("certificate", file);

    // Include certificate settings
    fd.append("name_x", form.name_x);
    fd.append("name_y", form.name_y);
    fd.append("font_size", form.font_size);
    fd.append("font_color", form.font_color);

    try {
      await axios.post(`${API_URL}/workshops`, fd);
      toast.success("Workshop created successfully with certificate settings");
      handleClose();
    } catch (err) {
      toast.error("Failed to create workshop");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setForm({
        title: "",
        date: "",
        name_x: 2250,
        name_y: 2150,
        font_size: 64,
        font_color: "#000000",
      });
      setFile(null);
      setPreview(null);
      setErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{fontWeight: 600}}>
          Create New Workshop
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Set up a new workshop and configure the certificate template
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <TextField
              label="Workshop Title"
              fullWidth
              required
              value={form.title}
              onChange={(e) => {
                setForm({...form, title: e.target.value});
                if (errors.title) setErrors({...errors, title: ""});
              }}
              variant="outlined"
              error={!!errors.title}
              helperText={errors.title || "Enter a descriptive title for the workshop"}
              placeholder="e.g., Introduction to Web Development"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Workshop Date"
              type="date"
              fullWidth
              required
              value={form.date}
              onChange={(e) => {
                setForm({...form, date: e.target.value});
                if (errors.date) setErrors({...errors, date: ""});
              }}
              variant="outlined"
              InputLabelProps={{shrink: true}}
              error={!!errors.date}
              helperText={errors.date || "Select the date when the workshop will be held"}
            />
          </Grid>

          {/* Certificate Upload */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom style={{fontWeight: 600}}>
              Certificate Template *
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              Upload an image that will be used as the certificate template
            </Typography>

            <Box mt={2}>
              <input
                accept="image/*"
                style={{display: "none"}}
                id="certificate-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="certificate-upload">
                <Paper
                  elevation={0}
                  style={{
                    border: `2px dashed ${errors.certificate ? "#ef4444" : "#cbd5e0"}`,
                    borderRadius: 12,
                    padding: 24,
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: preview ? "#f9fafb" : "#ffffff",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!preview) e.currentTarget.style.borderColor = "#f59e0b";
                  }}
                  onMouseLeave={(e) => {
                    if (!preview) e.currentTarget.style.borderColor = "#cbd5e0";
                  }}
                >
                  {preview ? (
                    <Box>
                      <img
                        src={preview}
                        alt="Certificate preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 300,
                          borderRadius: 8,
                          marginBottom: 16,
                        }}
                      />
                      <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                        <CheckIcon style={{color: "#10b981"}} />
                        <Typography variant="body2" style={{fontWeight: 600}}>
                          {file?.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {(file?.size / 1024).toFixed(2)} KB
                      </Typography>
                      <Box mt={2}>
                        <Button
                          size="small"
                          variant="outlined"
                          component="span"
                          style={{textTransform: "none"}}
                        >
                          Change Template
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box component="span">
                      <ImageIcon style={{fontSize: 48, color: "#9ca3af", marginBottom: 8}} />
                      <Typography variant="subtitle1" gutterBottom style={{fontWeight: 600}}>
                        Click to upload certificate template
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        PNG, JPG or JPEG (max 5MB)
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </label>
              {errors.certificate && (
                <Typography variant="caption" color="error" display="block" style={{marginTop: 8}}>
                  {errors.certificate}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Certificate Settings */}
          <Grid item xs={12}>
            <Divider style={{margin: "16px 0"}} />
            <Typography variant="subtitle2" gutterBottom style={{fontWeight: 600}}>
              Certificate Name Position & Styling
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              Configure where and how participant names will appear on the certificate
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography gutterBottom style={{fontWeight: 500, fontSize: "0.875rem"}}>
              Horizontal Position (X): {form.name_x}px
            </Typography>
            <Slider
              value={form.name_x}
              onChange={(e, val) => setForm({...form, name_x: val})}
              min={0}
              max={3000}
              step={10}
              valueLabelDisplay="auto"
              color="secondary"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography gutterBottom style={{fontWeight: 500, fontSize: "0.875rem"}}>
              Vertical Position (Y): {form.name_y}px
            </Typography>
            <Slider
              value={form.name_y}
              onChange={(e, val) => setForm({...form, name_y: val})}
              min={0}
              max={3000}
              step={10}
              valueLabelDisplay="auto"
              color="secondary"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography gutterBottom style={{fontWeight: 500, fontSize: "0.875rem"}}>
              Font Size: {form.font_size}px
            </Typography>
            <Slider
              value={form.font_size}
              onChange={(e, val) => setForm({...form, font_size: val})}
              min={12}
              max={120}
              step={2}
              valueLabelDisplay="auto"
              color="secondary"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography gutterBottom style={{fontWeight: 500, fontSize: "0.875rem"}}>
              Font Color
            </Typography>
            <TextField
              type="color"
              fullWidth
              value={form.font_color}
              onChange={(e) => setForm({...form, font_color: e.target.value})}
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={0} style={{padding: 16, backgroundColor: "#f0f9ff", borderRadius: 8}}>
              <Typography variant="caption" style={{fontWeight: 500}}>
                💡 <strong>Tip:</strong> You can adjust these settings later in the workshop details page and
                use the live preview to fine-tune the positioning.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{padding: 16}}>
        <Button onClick={handleClose} disabled={submitting} style={{textTransform: "none"}}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={submit}
          disabled={submitting}
          startIcon={submitting ? null : <UploadIcon />}
          style={{textTransform: "none", fontWeight: 600}}
        >
          {submitting ? "Creating..." : "Create Workshop"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
